<template>
  <div class="profile container-fluid">
    <button v-if="$userIsAdmin" class="profile__back btn btn-info" @click="$router.push('/talent')">
      <i class="fa fa-chevron-left"></i>
      <span>Back to {{ $starLabel | sentenceCase }} List</span>
    </button>
    <div class="profile__header">
      <div class="profile__image">
        <img :src="user.profile_image_url" :alt="user.name">
      </div>
      <div class="profile__text">
        <h1>{{ user.name }}</h1>
        <div class="profile__tagline">
          {{ user.profile.tagline }}
        </div>
        <div class="profile__buttons">
          <ladda-button
            button class="btn btn-primary" :click="handleDownloadPdfCallback">
            <i class="fa fa-cloud-download"></i> Download Bio
          </ladda-button>
            <router-link v-if="canEdit" tag="button" :to="{ name: 'talent.profile.show', params: { id: user.id, status: 'event' }}" class="btn btn-secondary">
              Edit profile
            </router-link>
        </div>
      </div>
    </div>

    <div class="profile__body">

      <div class="profile__section">
        <h2>About me</h2>
        <div v-html="user.profile.description"/>
      </div>

      <div class="profile__section">
        <div class="profile__row">
          <div class="profile__column">
            <h2>Based in</h2>
            <ul v-if="userBased" class="tags">
              <li><span class="tag">{{ user.based_in.name }}</span></li>
            </ul>
          </div>
          <div class="profile__column">
            <h2>Available in</h2>
            <ul v-if="user.locations" class="tags">
              <li v-for="location in user.locations_names" :key="location.id" >
                <span class="tag">{{ location.name }}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div class="profile__section" v-if="user.custom_fields && user.custom_fields.length">
        <div class="profile__row">
          <div class="profile__column">
            <h2>Custom Data</h2>
            <ul>
              <li v-for="field in user.custom_fields" :key="field.field">
                {{ field.name }}: {{ field.value }}
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div class="profile__section">
        <h2>Protected sponsors</h2>
        <ul v-if="sponsors" class="tags">
          <li v-for="sponsor in sponsors" :key="sponsor.id" >
            <span class="tag">{{ sponsor.name }}</span>
          </li>
        </ul>
      </div>

    </div>

  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import { notify } from 'utils'
import { helpers } from 'mixins/users'

export default {
  mixins: [helpers],

  props: ['id'],

  components: {
  },

  // data () {
  //   return {
  //     locationOptions: []
  //   }
  // },

  computed: {
    ...mapState('user', ['user']),
    ...mapGetters('user', ['haveUser']),
    ...mapState('location', ['locations']),

    canEdit () {
      if (this.$userIsGlobalAdmin) {
        return true
      }
      if (this.$userIsRegionalAdmin || this.$userIsSuperRegionalAdmin) {
        return this.user.region_id === this.$user.region_id
      }
      return false
    },
    onDefaultProfileView () {
      return new RegExp(/^([a-z]+\.)*profile\.show$/).test(this.$route.name)
    },
    userBased () {
      return this.user.based_in
    },
    sponsors () {
      const tags = this.user.tags ? this.user.tags : []
      return tags.filter(tag => {
        return tag.type === 'sponsor'
      })
    }
  },

  watch: {
    id (oldValue, newValue) {
      if (parseInt(oldValue) === parseInt(newValue)) {
        return
      }

      let id = this.id || this.$user.id

      this.getUser({ id })
    }
  },

  beforeDestroy () {
    this.resetUser()
  },

  created () {
    this.$validator.reset()

    let id = this.$route.params.id ? this.$route.params.id : this.$user.id

    this.getUser({id})
      .catch((error) => {
        if (error.response && error.response.status === 404) {
          notify.error('Could not find the user.')

          this.$router.push({name: 'dashboard', query: { src: 'talent-profile' }})
        }
      })
  },

  methods: {
    ...mapActions('user', ['getUser', 'resetUser']),
    ...mapActions('talent', ['getPdf']),

    handleDownloadPdfCallback () {
      return this.getPdf({ id: this.user.id })
        .then(({ data: { file, name } }) => {
          window.open(file, '_blank')
        })
    }
  }
}
</script>

<style lang="scss" scoped>
  @import '../../assets/sass/bootstrap/variables';
  .profile {
    margin-top: 50px;
    &__back {
      margin-bottom: 30px;
    }
    &__header {
      padding: 30px;
      background: var(--brand-primary);
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      position: relative;
      overflow: hidden;
      justify-content: center;
      text-align: center;
      @media (min-width: 992px) {
        flex-wrap: nowrap;
        justify-content: flex-start;
        text-align: left;
      }
      &::after {
        content: '';
        width: 50%;
        height: 100%;
        display: block;
        background-color: var(--brand-primary-dark);
        position: absolute;
        top: 0;
        right: 0;
        z-index: 0;
        transform: skewX(45deg) scaleX(2);
        transform-origin: top left;
      }
      & > * {
        position: relative;
        z-index: 1;
      }
    }
    &__image {
      border-radius: 50%;
      overflow: hidden;
      width: 180px;
    }
    &__text {
      color: var(--brand-text);
      @media (min-width: 640px) {
        padding: 0 60px;
      }
    }
     &__title {
      font-size: $font-size-h1;
      font-weight: 800;
      text-transform: uppercase;
      letter-spacing: 0.12em;
    }
    &__tagline {
      font-size: $font-size-h5;
      font-weight: 800;
      text-transform: uppercase;
      letter-spacing: 0.12em;
    }
    &__buttons {
      margin-top: 30px;
      .btn + .btn {
        @media (min-width: 768px) {
          margin-left: 15px;
        }
        @media (min-width: 767px) and (max-width: 1280px) {
          margin-left: 0;
        }
      }
      .btn {
        @media (max-width: 767px) {
          width: 100%;
          margin-top: 15px;
        }
        @media (min-width: 767px) and (max-width: 1280px) {
          width: 100%;
          margin-top: 15px;
        }
      }
      .btn-primary {
        background: var(--brand-text);
        border: 1px solid var(--brand-text);
        color: var(--brand-primary);
        &:hover,
        &:focus {
          border-color: var(--brand-text-dark);
          background: var(--brand-text-dark);
        }
      }
      .btn-secondary {
        background: transparent;
        border: 1px solid var(--brand-text);
        color: var(--brand-text);
        &:hover,
        &:focus {
        }
      }
    }
    &__body {
      margin: 60px 0;
    }
    &__section {
      & + & {
        width: 100%;
        margin-top: 60px;
      }
    }
    &__row {
      display: flex;
      flex-wrap: wrap;
      width: calc(100% + 60px);
      margin: 0 -30px;
      @media (min-width: 992px) {
        flex-wrap: nowrap;
      }
    }
    &__column {
      width: 100%;
      padding: 0 30px;
    }
  }
  .btn i {
    margin-right: 8px;
  }
</style>
